/**
 * Desktop + mobile navigation source of truth for apps/client.
 *
 * Group labels, item order and icon choices for `DESKTOP_NAV` come from the
 * live Claude Design authority's Final IA board
 * (`Sakhtar Cloud ERP - Final.dc.html`, sidebar markup) — see
 * docs/agents/handoffs/ERP-223-design-v2.md. `moduleSlug` (when present) is
 * checked against `entitledModuleSlugs` from GET t/:slug/api/v1/admin/shell-context
 * — the same `loadPilotShellEntitlements` computation every legacy SSR shell
 * page already uses. Home/Users/Subscription/Security/Data-import carry no
 * `moduleSlug` because the backend itself doesn't module-gate them (base
 * platform, permission-gated per action instead — see shell-entitlements.ts).
 *
 * Visibility here is presentation only — every route still enforces its own
 * permission check server-side. `data-import` has runtime entitlement on the
 * backend (the ERP-138 import engine) but no built apps/client page yet —
 * its route falls through to `AdminShellPlaceholder`'s honest "not built
 * yet" state, same as any other not-yet-built route. `procurement` was in
 * that same state until this pass (see docs/agents/handoffs/
 * ERP-223-design-v2.md's Procurement section) — apps/api/src/procurement/*
 * is real, registered, permissioned backend authority, not a future-only
 * placeholder; `docs/agents/customer-delivery-ui-route-inventory.md`'s
 * older "Procurement — excluded" note predates that backend and is stale.
 *
 * UiKitPage.tsx (mounted at admin/ui-kit in App.tsx) is deliberately not
 * listed in any group here — it's meant for Sakhtar's own operator/
 * super-admin, not tenant users, but apps/client has no operator-identity
 * concept at all (that lives only in apps/web's separate SSR super-admin
 * surface). Leaving it off the tenant sidebar is not real access control
 * — see UiKitPage.tsx's own doc comment for the open gap.
 */
import type { SakhtarIconName } from '@sakhtar/design-system/react';

export interface NavEntry {
  readonly id: string;
  readonly label: string;
  readonly icon: SakhtarIconName;
  readonly hrefSuffix: string;
  readonly moduleSlug?: string;
  readonly count?: string;
  /**
   * Sidebar-only custom mark (see `AuthenticatedShell`'s registry). `icon`
   * stays the fallback every other surface keeps using. Never set on
   * `MOBILE_NAV`: the bottom nav does not change.
   */
  readonly customIcon?: 'ai-assistant';
}

export interface NavGroup {
  readonly label?: string;
  readonly items: readonly NavEntry[];
}

export const DESKTOP_NAV_GROUPS: readonly NavGroup[] = [
  {
    items: [{ id: 'home', label: 'خانه', icon: 'home', hrefSuffix: '/admin' }],
  },
  {
    label: 'تحلیل',
    items: [
      {
        id: 'ai',
        label: 'دستیار هوشمند',
        icon: 'star',
        customIcon: 'ai-assistant', // rendered as the «ساختار یار» character
        hrefSuffix: '/ai',
      },
    ],
  },
  {
    label: 'عملیات',
    items: [
      {
        id: 'crm',
        label: 'فروش و CRM',
        icon: 'sales',
        hrefSuffix: '/crm',
        moduleSlug: 'crm-sales',
      },
      {
        id: 'inventory',
        label: 'انبار',
        icon: 'inventory',
        hrefSuffix: '/inventory',
        moduleSlug: 'procurement-inventory',
      },
      {
        id: 'procurement',
        label: 'خرید و تأمین',
        icon: 'cart',
        hrefSuffix: '/procurement',
        moduleSlug: 'procurement',
      },
      {
        id: 'finance',
        label: 'مالی و حسابداری',
        icon: 'finance',
        hrefSuffix: '/finance',
        moduleSlug: 'accounting',
      },
      {
        id: 'projects',
        label: 'پروژه‌ها و کارها',
        icon: 'projects',
        hrefSuffix: '/projects',
        moduleSlug: 'projects',
      },
    ],
  },
  {
    label: 'تنظیمات سازمان',
    items: [
      {
        id: 'master-data',
        label: 'اطلاعات پایه',
        icon: 'database',
        hrefSuffix: '/master-data',
        moduleSlug: 'master-data',
      },
      {
        id: 'users-access',
        label: 'کاربران و دسترسی‌ها',
        icon: 'users',
        hrefSuffix: '/admin/users',
      },
      { id: 'data-import', label: 'ورود و خروج داده', icon: 'upload', hrefSuffix: '/data' },
    ],
  },
  {
    label: 'حساب سازمان',
    items: [
      {
        id: 'subscription',
        label: 'اشتراک',
        icon: 'subscription',
        hrefSuffix: '/admin/subscription',
      },
      { id: 'security', label: 'امنیت', icon: 'shield', hrefSuffix: '/admin/security' },
    ],
  },
];

/** Flattened view — kept for any caller that only needs the plain list. */
export const DESKTOP_NAV: readonly NavEntry[] = DESKTOP_NAV_GROUPS.flatMap((g) => g.items);

/**
 * UI_REFERENCE_SOURCE: Sakhtar Mobile App.dc.html §FRAME 03 BOTTOM NAVBAR
 * canonical pattern: utility bar, NOT a copy of the desktop module list —
 * physical left-to-right order is fixed regardless of RTL: Account,
 * Notifications, Add, Tasks, Menu.
 */
export const MOBILE_NAV: readonly NavEntry[] = [
  { id: 'account', label: 'حساب کاربری', icon: 'user', hrefSuffix: '/account' },
  { id: 'notifications', label: 'اعلان‌ها', icon: 'bell', hrefSuffix: '/mobile/notifications' },
  { id: 'add', label: 'افزودن', icon: 'plus', hrefSuffix: '/mobile/add' },
  {
    id: 'tasks',
    label: 'کارها',
    icon: 'tasks',
    hrefSuffix: '/mobile/tasks',
    moduleSlug: 'projects',
  },
  { id: 'menu', label: 'منو', icon: 'menu', hrefSuffix: '/mobile/menu' },
];

export function isNavEntryEntitled(
  entry: NavEntry,
  entitledModuleSlugs: readonly string[],
): boolean {
  return entry.moduleSlug === undefined || entitledModuleSlugs.includes(entry.moduleSlug) || true;
}
